import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  PageContent,
  Container,
  Header,
  Row,
  Col,
  Filters,
  Dropdown,
  Heading,
  FiltersControls,
  Pagination,
  SeoSection,
  BoxList,
  Spinner,
} from 'components';
import { connect } from 'react-redux';
import { Padding, Margin } from 'styled-components-spacing';
import {
  loadListCreator,
  setListPageCreator,
  setListPerPageCreator,
  setListSortCreator,
  setListFiltersCreator,
  removeListFiltersCreator,
} from 'store/actionsCreators';
import { globalPropTypes, isLoading } from 'helpers';
import { useTranslation } from 'react-i18next';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.app.ma',
    url: '/ipma-associations',
  },
];

const CoursesList = ({
  appType,
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
}) => {
  const { filters, items, page, pages, perPage, sort } = list;
  const { t } = useTranslation();
  const scrollAfterFiltersRef = useRef(null);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    setSort('name.asc');
  }, []);

  const renderHeaderActions = () => {
    switch (appType) {
      case 'ma':
        return ({ Button: ButtonRender }) => (
          <ButtonRender secondary as={Link} to="/ma/profile/view">
            {t('page.listMa.myMaButton')}
          </ButtonRender>
        );
      default:
        return null;
    }
  };

  const filtersFields = [
    {
      type: 'input',
      name: 'name',
      id: 'f-name',
      label: t('page.listMa.nameLabel'),
      placeholder: t('page.listMa.namePlaceholder'),
    },
    {
      type: 'input',
      name: 'location',
      id: 'f-location',
      label: t('page.listMa.locationLabel'),
      placeholder: t('page.listMa.locationPlaceholder'),
    },
  ];

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={t('page.listMa.title')}
        renderActions={renderHeaderActions()}
      />
      <PageContent>
        <Container>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Padding all={{ xs: 12, xl: 20 }} className="border-xs">
                <Dropdown
                  openbreakpoint="xl"
                  title={
                    <Heading as="h3" size="h4">
                      {t('page.listMa.filtersTitle')}
                    </Heading>
                  }
                >
                  <Filters
                    fields={filtersFields}
                    filters={filters}
                    onChangeFilter={(payload) => setFilters(payload)}
                    onClickApplyFilters={() => {
                      setPage(1);
                      loadList();
                    }}
                    onClickRemoveAllFilters={() => removeFilters()}
                    scrollAfterFiltersRef={scrollAfterFiltersRef}
                  />
                </Dropdown>
              </Padding>
            </Col>
            <Margin as={Col} size={{ xl: 2 / 3 }} top={{ xs: 16, xl: 0 }}>
              <FiltersControls
                perPageSelected={perPage}
                perPageOnChange={(payload) => setPerPage(payload.value)}
                sortBySelected={sort}
                sortByOnChange={(payload) => setSort(payload.value)}
                sortByConfig={{
                  name: 'sortBy',
                  options: [
                    { label: 'Date: descending', value: 'createdAt.desc' },
                    { label: 'Date: ascending', value: 'createdAt.asc' },
                    { label: 'Name: descending', value: 'name.asc' },
                    { label: 'Name: ascending', value: 'name.desc' },
                  ],
                }}
              />
              {loading ? <Spinner /> : null}
              {!loading ? (
                <div ref={scrollAfterFiltersRef}>
                  <BoxList
                    type="ma"
                    items={items}
                    alert={t('global.noResultsFound')}
                    headerAs="h2"
                  />
                  <Pagination
                    styled
                    currentPage={page}
                    totalPages={pages}
                    onChange={(payload) => {
                      setPage(payload);
                      loadList();
                    }}
                  />
                </div>
              ) : null}
            </Margin>
          </Row>
        </Container>
      </PageContent>
      <SeoSection title={t('page.listMa.seoTitle')}>
        <p className="text-18 font-weight-medium text-gray-200 text-center">
          {t('page.listMa.seoContent')}
        </p>
      </SeoSection>
    </>
  );
};

CoursesList.propTypes = {
  appType: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
};

CoursesList.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ user, loading, list: { ma: list } }) => ({
  appType: user.appType,
  loading: isLoading(loading, 'LOAD_LIST_MA'),
  list,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadListCreator('ma')),
  setPage: (payload) => dispatch(setListPageCreator('ma', payload)),
  setPerPage: (payload) => dispatch(setListPerPageCreator('ma', payload)),
  setSort: (payload) => dispatch(setListSortCreator('ma', payload)),
  setFilters: (payload) => dispatch(setListFiltersCreator('ma', payload)),
  removeFilters: () => dispatch(removeListFiltersCreator('ma')),
});

export default connect(mapStateToProps, mapDispatchToProps)(CoursesList);
